import React, { useState, useEffect, useRef } from 'react';
import {
  ArrowLeft,
  Eye,
  EyeOff,
  Loader2,
  Send,
  Shield,
  CheckCircle,
  Building2,
  Star,
  Lock,
  UserCheck,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabaseClient';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import QRCode from 'qrcode';
import AuthPlatformNotice from '../components/AuthPlatformNotice';

interface FormData {
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  company: string;
  title: string;
  country_region: string;
  company_size: string;
  timeline: string;
  budget_range: string;
  password: string;
  confirmPassword: string;
}

const inputCls =
  'block w-full rounded-lg border border-gray-300 bg-white text-gray-900 px-3 py-2 text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#40E0D0] focus:border-transparent transition-colors';
const labelCls = 'block text-xs font-semibold text-gray-600 mb-1 tracking-wide';
const sectionCls = 'bg-white border border-gray-200 rounded-xl p-5 shadow-sm';

type SignupMode = 'pro' | 'free';

export default function ProClientSignup() {
  const navigate = useNavigate();

  const [formData, setFormData] = useState<FormData>({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    company: '',
    title: '',
    country_region: '',
    company_size: '',
    timeline: '',
    budget_range: '',
    password: '',
    confirmPassword: '',
  });

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [fieldError, setFieldError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  // Modal state
  const [showPayPalConfirmModal, setShowPayPalConfirmModal] = useState(false);
  // 'idle' | 'processing' | 'done'
  const [modalState, setModalState] = useState<'idle' | 'processing' | 'done'>('idle');

  // 2FA state
  const [qrCodeUrl, setQrCodeUrl] = useState<string | null>(null);
  const [manualEntryKey, setManualEntryKey] = useState<string | null>(null);
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [twoFAReady, setTwoFAReady] = useState(false);
  const [qrGeneratedForEmail, setQrGeneratedForEmail] = useState<string>('');
  const [emailChangedAfterScan, setEmailChangedAfterScan] = useState(false);

  const [pwStrength, setPwStrength] = useState(0);

  // Ref so the processSignup closure always sees latest formData/manualEntryKey/twoFactorCode
  const formRef = useRef(formData);
  formRef.current = formData;
  const secretRef = useRef(manualEntryKey);
  secretRef.current = manualEntryKey;
  const codeRef = useRef(twoFactorCode);
  codeRef.current = twoFactorCode;

  useEffect(() => {
    window.scrollTo(0, 0);
    const secret = generateRandomSecret();
    setManualEntryKey(secret);
  }, []);

  useEffect(() => {
    if (!formData.email) return;

    if (qrGeneratedForEmail && qrGeneratedForEmail !== formData.email) {
      const newSecret = generateRandomSecret();
      setManualEntryKey(newSecret);
      setTwoFactorCode('');
      setTwoFAReady(false);
      setEmailChangedAfterScan(true);
      return;
    }

    if (manualEntryKey) {
      generateQrCode(manualEntryKey, formData.email).then(() => {
        setQrGeneratedForEmail(formData.email);
        setEmailChangedAfterScan(false);
      });
    }
  }, [manualEntryKey, formData.email]);

  const calcStrength = (pw: string) => {
    let s = 0;
    if (pw.length >= 8) s++;
    if (/[A-Z]/.test(pw)) s++;
    if (/[0-9]/.test(pw)) s++;
    if (/[^A-Za-z0-9]/.test(pw)) s++;
    return s;
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (name === 'password') {
      setFieldError('');
      setPwStrength(calcStrength(value));
    }
    if (name === 'confirmPassword') setFieldError('');
  };

  const generateRandomSecret = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ234567';
    let secret = '';
    for (let i = 0; i < 32; i++) {
      secret += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return secret;
  };

  const generateQrCode = async (secret: string, email: string) => {
    if (!email) return;
    const otpauthUrl = `otpauth://totp/PerplexityATS:${encodeURIComponent(email)}?secret=${secret}&issuer=PerplexityATS`;
    const url = await QRCode.toDataURL(otpauthUrl);
    setQrCodeUrl(url);
  };

  const strengthLabel = ['', 'Weak', 'Fair', 'Good', 'Strong'];
  const strengthColor = ['', 'bg-red-500', 'bg-yellow-400', 'bg-blue-400', 'bg-green-500'];

  // Validates required fields and returns an error string or empty string.
  const validate = (): string => {
    const fd = formRef.current;
    if (!fd.first_name || !fd.last_name || !fd.email || !fd.password) {
      return 'Please complete all required fields.';
    }
    if (fd.password.length < 6) return 'Password must be at least 6 characters.';
    if (fd.password !== fd.confirmPassword) return 'Passwords do not match.';
    if (!secretRef.current || !codeRef.current) {
      return 'Google Authenticator setup is required. Scan the QR code and enter your 6-digit code.';
    }
    if (codeRef.current.replace(/\s/g, '').length !== 6) {
      return 'Authenticator code must be exactly 6 digits.';
    }
    return '';
  };

  // Core signup call — called with the chosen plan.
  // Returns { success: boolean; error?: string }
  const processSignup = async (mode: SignupMode): Promise<{ success: boolean; error?: string }> => {
    const fd = formRef.current;
    const secret = secretRef.current;
    const code = codeRef.current;

    const payload: Record<string, unknown> = {
      email: fd.email,
      password: fd.password,
      first_name: fd.first_name,
      last_name: fd.last_name,
      phone: fd.phone,
      title: fd.title,
      country_region: fd.country_region,
      company: fd.company,
      company_size: fd.company_size,
      timeline: fd.timeline,
      budget_range: fd.budget_range,
      plan: mode,
      pending_checkout: mode === 'pro',
      twofa_secret: secret,
      twofa_code: code?.replace(/\s/g, ''),
    };

    const response = await fetch(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/client-signup-complete`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify(payload),
      }
    );

    if (!response.ok) {
      const errData = await response.json();
      return { success: false, error: errData.error || 'Failed to complete signup' };
    }

    // Sign in after the edge function creates the account
    const loginRes = await supabase.auth.signInWithPassword({
      email: fd.email,
      password: fd.password,
    });

    if (loginRes.error || !loginRes.data.session) {
      return { success: false, error: 'Account created but auto-login failed. Please log in manually.' };
    }

    // Stamp twofa_verified=true via service role (edge function pattern) is already
    // handled during signup (is_2fa_enabled=true + twofa_verified stamped in user_metadata
    // by the edge function). We must NOT call supabase.auth.updateUser here because
    // that fires USER_UPDATED inside a fresh SIGNED_IN event and can loop.
    // Instead, refresh once so PrivateRoute sees the latest JWT metadata.
    await supabase.auth.refreshSession();

    return { success: true };
  };

  // "Create Pro Account & Continue to PayPal" — validates form then opens the confirm modal.
  // Resets any stale submission state from a previous attempt so the modal buttons are always fresh.
  const handleOpenModal = () => {
    setSubmitError('');
    setFieldError('');
    // Reset stale state from any prior failed attempt so isProcessing is false when modal opens
    setIsSubmitting(false);
    setModalState('idle');

    const err = validate();
    if (err) {
      setFieldError(err);
      setTimeout(() => {
        document.querySelector('[data-field-error]')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 50);
      return;
    }

    setShowPayPalConfirmModal(true);
  };

  // User confirmed "Yes, continue to PayPal" inside the modal
  const handleConfirmPro = async () => {
    setModalState('processing');
    setSubmitError('');
    setIsSubmitting(true);

    try {
      const result = await processSignup('pro');
      if (!result.success) {
        setSubmitError(result.error || 'Signup failed. Please try again.');
        setModalState('idle');
        setShowPayPalConfirmModal(false);
        return;
      }
      // Auth is live — navigate to checkout; PrivateRoute + BillingGateRoute will
      // confirm the session before rendering the PayPal buttons.
      setModalState('done');
      setShowPayPalConfirmModal(false);
      navigate('/workday-client/checkout');
    } catch (err: unknown) {
      setSubmitError(err instanceof Error ? err.message : 'Failed to create account.');
      setModalState('idle');
      setShowPayPalConfirmModal(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  // User clicked "No, create a free account instead" or closed the modal
  const handleFreeAccount = async () => {
    setModalState('processing');
    setSubmitError('');
    setIsSubmitting(true);

    try {
      const result = await processSignup('free');
      if (!result.success) {
        setSubmitError(result.error || 'Signup failed. Please try again.');
        setModalState('idle');
        setShowPayPalConfirmModal(false);
        return;
      }
      setModalState('done');
      setShowPayPalConfirmModal(false);
      // Free account goes straight to the client dashboard
      navigate('/workday-client/dashboard');
    } catch (err: unknown) {
      setSubmitError(err instanceof Error ? err.message : 'Failed to create account.');
      setModalState('idle');
      setShowPayPalConfirmModal(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  // isProcessing gates the modal buttons only — the main "open modal" button should never
  // be disabled by processing state (it just opens the confirm dialog, no async work).
  const isProcessing = modalState === 'processing' || isSubmitting;

  return (
    <div className="min-h-screen bg-gray-50 text-gray-900">
      <Navbar />

      {/* Header */}
      <div className="bg-black pt-32 pb-12">
        <div className="max-w-2xl mx-auto px-4">
          <button
            onClick={() => navigate('/pricing')}
            className="flex items-center text-gray-400 hover:text-white mb-8 transition-colors text-sm group"
          >
            <ArrowLeft className="w-4 h-4 mr-1.5 group-hover:-translate-x-1 transition-transform" />
            Back to Pricing
          </button>

          <div className="text-center mb-6">
            <div className="inline-flex items-center gap-2 bg-[#40E0D0]/10 border border-[#40E0D0]/40 text-[#40E0D0] text-xs font-bold px-4 py-1.5 rounded-full tracking-widest uppercase mb-3">
              <Star className="w-3.5 h-3.5" />
              PerplexityATS Pro — $499/mo
            </div>
            <h1 className="text-3xl sm:text-4xl font-bold mb-3 bg-gradient-to-r from-[#40E0D0] to-[#0066FF] bg-clip-text text-transparent">
              Create Your Account &amp; Activate Pro
            </h1>
            <p className="text-gray-400 text-sm max-w-lg mx-auto">
              Full access to the Workday talent pool, candidate scheduling, and priority support. 3 seats and 3 free job postings included.
            </p>
          </div>

          {/* Pricing clarification banner */}
          <div className="bg-amber-900/30 border border-amber-500/40 text-amber-300 text-xs rounded-lg px-4 py-3 text-center max-w-lg mx-auto">
            <Lock className="w-3.5 h-3.5 inline-block mr-1.5 mb-0.5" />
            Your account is created first. Pro billing starts only after completing secure checkout.
          </div>

          {/* Plan feature pills */}
          <div className="flex flex-wrap justify-center gap-3 mt-5">
            {['3 Starting Seats', '3 Free Job Postings', 'Full Talent Pool Access', 'Priority Support'].map((f) => (
              <div key={f} className="flex items-center gap-1.5 text-xs text-gray-300 bg-white/5 border border-white/10 rounded-full px-3 py-1">
                <CheckCircle className="w-3.5 h-3.5 text-[#40E0D0] flex-shrink-0" />
                {f}
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 pt-10 pb-24">
        {/* NOT a <form> — avoids implicit submit interfering with our button handlers */}
        <div className="space-y-4">

          {/* Contact Information */}
          <div className={sectionCls}>
            <h2 className="text-base font-bold text-gray-800 mb-4 flex items-center gap-2">
              <Building2 className="w-4 h-4 text-[#40E0D0]" />
              Client Contact Information
            </h2>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelCls}>First Name *</label>
                <input type="text" name="first_name" required value={formData.first_name} onChange={handleChange} className={inputCls} placeholder="Jane" />
              </div>
              <div>
                <label className={labelCls}>Last Name *</label>
                <input type="text" name="last_name" required value={formData.last_name} onChange={handleChange} className={inputCls} placeholder="Smith" />
              </div>
              <div>
                <label className={labelCls}>Email *</label>
                <input type="email" name="email" required value={formData.email} onChange={handleChange} className={inputCls} placeholder="jane@company.com" />
              </div>
              <div>
                <label className={labelCls}>Phone *</label>
                <input type="tel" name="phone" required value={formData.phone} onChange={handleChange} className={inputCls} placeholder="+1 (555) 000-0000" />
              </div>
              <div>
                <label className={labelCls}>Company *</label>
                <input type="text" name="company" required value={formData.company} onChange={handleChange} className={inputCls} placeholder="Acme Corp" />
              </div>
              <div>
                <label className={labelCls}>Your Title *</label>
                <input type="text" name="title" required value={formData.title} onChange={handleChange} className={inputCls} placeholder="HR Director" />
              </div>
              <div className="col-span-2">
                <label className={labelCls}>Country / Region *</label>
                <input type="text" name="country_region" required value={formData.country_region} onChange={handleChange} className={inputCls} placeholder="United States" />
              </div>
            </div>
          </div>

          {/* Organization needs */}
          <div className={sectionCls}>
            <h2 className="text-base font-bold text-gray-800 mb-4">Organization &amp; Workday Service Needs</h2>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelCls}>Company Size *</label>
                <select name="company_size" required value={formData.company_size} onChange={handleChange} className={inputCls}>
                  <option value="">Select company size</option>
                  <option value="1-50">1–50 employees</option>
                  <option value="51-200">51–200 employees</option>
                  <option value="201-1000">201–1,000 employees</option>
                  <option value="1000+">1,000+ employees</option>
                </select>
              </div>
              <div>
                <label className={labelCls}>Project Timeline *</label>
                <select name="timeline" required value={formData.timeline} onChange={handleChange} className={inputCls}>
                  <option value="">Select timeline</option>
                  <option value="immediate">Immediate</option>
                  <option value="1-month">Within 1 month</option>
                  <option value="3-months">Within 3 months</option>
                  <option value="ongoing">Ongoing service needs</option>
                </select>
              </div>
              <div className="col-span-2">
                <label className={labelCls}>Budget Range (per hour)</label>
                <input type="text" name="budget_range" value={formData.budget_range} onChange={handleChange} className={inputCls} placeholder="e.g. $75–$125/hr" />
              </div>
            </div>
          </div>

          {/* Account Setup */}
          <div className={sectionCls}>
            <h2 className="text-base font-bold text-gray-800 mb-4 flex items-center gap-2">
              <Shield className="w-4 h-4 text-[#40E0D0]" />
              Account Setup
            </h2>

            {fieldError && (
              <div data-field-error className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-3 py-2.5 text-xs mb-3">
                {fieldError}
              </div>
            )}

            <div className="grid grid-cols-2 gap-3 mb-5">
              <div>
                <label className={labelCls}>Password *</label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    required
                    value={formData.password}
                    onChange={handleChange}
                    className={`${inputCls} pr-9`}
                    placeholder="Min. 6 characters"
                  />
                  <button type="button" onClick={() => setShowPassword((v) => !v)} className="absolute inset-y-0 right-0 flex items-center pr-2.5 text-gray-400 hover:text-gray-600">
                    {showPassword ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                  </button>
                </div>
                {formData.password.length > 0 && (
                  <div className="mt-1.5">
                    <div className="flex gap-1 mb-0.5">
                      {[1, 2, 3, 4].map((i) => (
                        <div key={i} className={`h-1 flex-1 rounded-full transition-colors ${pwStrength >= i ? strengthColor[pwStrength] : 'bg-gray-200'}`} />
                      ))}
                    </div>
                    <p className="text-xs text-gray-500">
                      Strength: <span className={`font-semibold ${pwStrength >= 3 ? 'text-green-600' : pwStrength === 2 ? 'text-yellow-600' : 'text-red-600'}`}>{strengthLabel[pwStrength]}</span>
                    </p>
                  </div>
                )}
              </div>
              <div>
                <label className={labelCls}>Confirm Password *</label>
                <div className="relative">
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    name="confirmPassword"
                    required
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    className={`${inputCls} pr-9`}
                    placeholder="Re-enter password"
                  />
                  <button type="button" onClick={() => setShowConfirmPassword((v) => !v)} className="absolute inset-y-0 right-0 flex items-center pr-2.5 text-gray-400 hover:text-gray-600">
                    {showConfirmPassword ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                  </button>
                </div>
                {formData.confirmPassword.length > 0 && (
                  <p className={`text-xs mt-1.5 ${formData.password === formData.confirmPassword ? 'text-green-600' : 'text-red-600'}`}>
                    {formData.password === formData.confirmPassword ? '✓ Passwords match' : 'Passwords do not match'}
                  </p>
                )}
              </div>
            </div>

            {/* 2FA — Required */}
            <div className="border-t border-gray-100 pt-4">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <p className="text-sm font-bold text-gray-800 flex items-center gap-1.5">
                    <Shield className="w-4 h-4 text-[#40E0D0]" />
                    Google Authenticator Setup
                    <span className="ml-1 text-xs font-semibold text-red-600 bg-red-50 border border-red-200 px-1.5 py-0.5 rounded-full">Required</span>
                  </p>
                  <p className="text-xs text-gray-500 mt-0.5">Scan the QR code to secure your account before continuing.</p>
                </div>
                {twoFAReady && (
                  <span className="flex-shrink-0 inline-flex items-center gap-1 text-xs text-green-700 font-semibold bg-green-50 border border-green-200 px-2 py-1 rounded-full">
                    <CheckCircle className="w-3 h-3" /> Verified
                  </span>
                )}
              </div>

              {!qrCodeUrl && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
                  <p className="text-xs font-semibold text-amber-800 text-center mb-2">
                    Don't have Google Authenticator? Download it first:
                  </p>
                  <div className="flex flex-col sm:flex-row gap-2 justify-center">
                    <a href="https://play.google.com/store/apps/details?id=com.google.android.apps.authenticator2" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 bg-black text-white px-3 py-2 rounded-lg hover:bg-gray-800 transition-colors text-xs font-medium">
                      Google Play (Android)
                    </a>
                    <a href="https://apps.apple.com/us/app/google-authenticator/id388497605" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 bg-black text-white px-3 py-2 rounded-lg hover:bg-gray-800 transition-colors text-xs font-medium">
                      App Store (iOS)
                    </a>
                  </div>
                </div>
              )}

              {qrCodeUrl ? (
                <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
                  <h4 className="text-sm font-bold text-gray-800 text-center mb-3">
                    Step 1 — Scan with Google Authenticator
                  </h4>

                  {emailChangedAfterScan && (
                    <div className="bg-red-50 border border-red-300 text-red-700 rounded-lg px-3 py-2.5 text-xs mb-3 flex items-start gap-2">
                      <svg className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                      </svg>
                      <span>
                        <strong>Email changed — please re-scan this new QR code.</strong> A new authenticator key was generated. Delete any previous entry in your app and scan this updated code.
                      </span>
                    </div>
                  )}

                  <div className="flex justify-center mb-3">
                    <div className="p-2 bg-white rounded-xl border border-gray-200 shadow-sm">
                      <img src={qrCodeUrl} alt="2FA QR Code" className="w-36 h-36" />
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 text-center mb-1">
                    Open Google Authenticator → tap <strong className="text-gray-700">+</strong> → <strong className="text-gray-700">Scan a QR code</strong>
                  </p>
                  {manualEntryKey && (
                    <p className="text-xs text-gray-400 text-center mb-4">
                      Manual key: <span className="font-mono text-gray-600 select-all">{manualEntryKey}</span>
                    </p>
                  )}

                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1 text-center">
                      Step 2 — Enter the 6-digit code from your app
                    </label>
                    <p className="text-xs text-amber-600 text-center mb-2">
                      Codes refresh every 30 seconds — enter a fresh code right before clicking Sign Up.
                    </p>
                    <input
                      type="text"
                      inputMode="numeric"
                      maxLength={6}
                      value={twoFactorCode}
                      onChange={(e) => {
                        const val = e.target.value.replace(/\D/g, '');
                        setTwoFactorCode(val);
                        setTwoFAReady(val.length === 6);
                        setFieldError('');
                      }}
                      placeholder="000000"
                      className={`block w-full text-center text-2xl font-mono tracking-[0.4em] rounded-xl border-2 px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#40E0D0] bg-white text-gray-900 transition-colors ${
                        twoFactorCode.length === 6 ? 'border-green-400' : 'border-gray-300 focus:border-[#40E0D0]'
                      }`}
                    />
                    {twoFactorCode.length === 6 && (
                      <p className="text-green-600 text-xs mt-1.5 text-center flex items-center justify-center gap-1">
                        <Shield className="w-3 h-3" /> Code ready — click Sign Up to continue
                      </p>
                    )}
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-center py-6 text-gray-400 text-xs">
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  Generating QR code — enter your email above first
                </div>
              )}
            </div>

            {/* Submit */}
            <div className="mt-5 pt-4 border-t border-gray-100">
              {submitError && (
                <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-3 py-2.5 text-xs mb-3">
                  {submitError}
                </div>
              )}
              <button
                type="button"
                onClick={handleOpenModal}
                className="w-full bg-gradient-to-r from-[#40E0D0] to-[#0066FF] text-white font-bold px-6 py-3 rounded-xl inline-flex items-center justify-center gap-2 transition-all transform hover:scale-[1.02] shadow-md hover:shadow-lg text-sm"
              >
                <Send className="w-4 h-4" />
                Create Pro Account &amp; Continue to PayPal
              </button>
              <p className="text-center text-gray-400 text-xs mt-3">
                Your account is created immediately. If you don't complete payment, your account will remain on the Free plan with limited access until you upgrade.
              </p>
              <p className="text-center text-gray-400 text-xs mt-1.5">
                By signing up you agree to our{' '}
                <a href="/terms" className="text-[#0099aa] hover:underline">Terms of Service</a> and{' '}
                <a href="/privacy" className="text-[#0099aa] hover:underline">Privacy Policy</a>.
              </p>
            </div>
          </div>
        </div>
      </div>

      <AuthPlatformNotice />

      <Footer />

      {/* PayPal Confirmation Modal — rendered at root level, outside all layout containers */}
      {showPayPalConfirmModal && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
          {/* Backdrop — clicking while not processing closes as free account prompt */}
          <div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={() => { if (!isProcessing) { setShowPayPalConfirmModal(false); } }}
          />

          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 z-10">
            {/* Close button */}
            {!isProcessing && (
              <button
                type="button"
                onClick={() => setShowPayPalConfirmModal(false)}
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
                aria-label="Close"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            )}

            {/* Icon + heading */}
            <div className="flex items-center gap-3 mb-4">
              <div className="bg-[#40E0D0]/10 border border-[#40E0D0]/30 rounded-full p-2.5 flex-shrink-0">
                <Lock className="w-5 h-5 text-[#40E0D0]" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-gray-900">Before you continue to PayPal</h2>
                <p className="text-xs text-gray-500 mt-0.5">Review what happens next</p>
              </div>
            </div>

            <p className="text-sm text-gray-600 leading-relaxed mb-2">
              Clicking <strong className="text-gray-800">"Yes, create my Pro account"</strong> will:
            </p>
            <ul className="text-sm text-gray-600 space-y-1.5 mb-4 pl-1">
              <li className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-[#40E0D0] flex-shrink-0 mt-0.5" />
                Create your PerplexityATS account with 2FA enabled
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-[#40E0D0] flex-shrink-0 mt-0.5" />
                Redirect you to PayPal to complete the $499/mo subscription
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-[#40E0D0] flex-shrink-0 mt-0.5" />
                Unlock full Pro features immediately after payment is confirmed
              </li>
            </ul>

            <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2.5 text-xs text-amber-700 mb-5">
              If you don't complete payment after being redirected, your account will be created as a <strong>Free account</strong> automatically.
            </div>

            <div className="flex flex-col gap-3">
              {/* Primary: confirm Pro */}
              <button
                type="button"
                disabled={isProcessing}
                onClick={handleConfirmPro}
                className="w-full bg-gradient-to-r from-[#40E0D0] to-[#0066FF] text-white font-bold px-6 py-3 rounded-xl inline-flex items-center justify-center gap-2 transition-all hover:scale-[1.02] shadow-md disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:scale-100 text-sm"
              >
                {isProcessing && modalState === 'processing' ? (
                  <>
                    <Loader2 className="animate-spin w-4 h-4" />
                    Creating your account...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4" />
                    Yes, create my Pro account — $499/mo
                  </>
                )}
              </button>

              {/* Secondary: create free account instead */}
              <button
                type="button"
                disabled={isProcessing}
                onClick={handleFreeAccount}
                className="w-full border border-gray-200 text-gray-700 font-medium px-6 py-2.5 rounded-xl hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm inline-flex items-center justify-center gap-2"
              >
                {isProcessing && modalState === 'processing' ? (
                  <>
                    <Loader2 className="animate-spin w-4 h-4" />
                    Creating free account...
                  </>
                ) : (
                  <>
                    <UserCheck className="w-4 h-4 text-gray-500" />
                    No thanks, just create a free account
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
